import React, { useState, useContext } from "react";
import { Button, TextField, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "./LoginScreen.css";
import "../../CSS/fonts.css";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../contexts/GlobalContext";

function SignIn() {
  let devUserName = "";
  let devPassword = "";

  if (process.env.NODE_ENV === "development") {
    devUserName = process.env.REACT_APP_DEV_USER_NAME;
    devPassword = process.env.REACT_APP_DEV_USER_PASSWORD;
  } else {
    devUserName = "";
    devPassword = "";
  }

  const [username, setUsername] = useState(devUserName);
  const [password, setPassword] = useState(devPassword);
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false); // State to track loading status
  const [twoFactorCode, setTwoFactorCode] = useState(""); // MFA code
  const [mfaRequired, setMfaRequired] = useState(false); // MFA step
  const navigate = useNavigate();
  const { login } = useAuth();
  const { fetchUserData } = useContext(GlobalContext);
  const theme = useTheme();

  const handleLogin = async () => {
    setLoginError("");
    setLoading(true); // Start loading
    try {
      const requestBody = {
        email: username,
        password: password,
      };
      if (mfaRequired && twoFactorCode) {
        requestBody.twoFactorCode = twoFactorCode;
      }
      const response = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 429) {
        setLoginError("Too many login attempts. Please try again later.");
        return;
      }

      // SEC-014: only RequiresTwoFactor is special-cased; other failures share one message.
      if (response.status === 401) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch {
          errorData = {};
        }

        if (errorData.detail === "RequiresTwoFactor") {
          setMfaRequired(true);
          setLoginError("Voer uw authenticatiecode in.");
          setTwoFactorCode("");
          return;
        }

        setLoginError("Invalid email or password.");
        setMfaRequired(false);
        return;
      }

      if (!response.ok) {
        setLoginError("Invalid email or password.");
        setMfaRequired(false);
        return;
      }

      const data = await response.json();
      await login(data.accessToken, data.refreshToken, data.expiresIn);
      await fetchUserData(); // Fetch user data after successful login
      navigate("/dashboard", { replace: true }); // Redirect to dashboard after login
    } catch (error) {
      setLoginError("Invalid email or password.");
      setMfaRequired(false);
    } finally {
      setLoading(false); // Stop loading regardless of outcome
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading) {
      event.preventDefault();
      handleLogin();
    }
  };

  return (
    <div className="split-screen">
      <div className="left-pane">
        <div className="content">
          <img src="/static/img/delta-refund-solutions-blue.png" alt="Delta Refund Solutions" className="login-logo" />
          <div className="main-text-container">
            <p className="login-instruction" style={{ fontFamily: "'Helvetica Neue Light', sans-serif" }}>
              Welkom bij het DWS portaal!
              <br />
              Gelieve in te loggen.
            </p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!loading) handleLogin();
            }}
            className="button-container"
            style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
          >
            {loginError && (
              <div
                style={{
                  fontFamily: "'Helvetica Neue Light', sans-serif",
                  fontSize: "1rem",
                  marginBottom: "1rem",
                  alignSelf: "flex-start",
                  color: theme.palette.mode === "dark" ? "#ff6b6b" : "red",
                }}
              >
                {loginError}
              </div>
            )}
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{ marginBottom: "1rem" }}
              disabled={loading || mfaRequired}
              inputProps={{ tabIndex: 0, "aria-label": "Gebruikersnaam" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.mode === "dark" ? "#fff" : undefined,
                  "& fieldset": {
                    borderColor: theme.palette.mode === "dark" ? "#ccc" : undefined,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.mode === "dark" ? "#999" : undefined,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.mode === "dark" ? theme.palette.primary.main : undefined,
                  },
                  "&.Mui-disabled fieldset": {
                    borderColor: theme.palette.mode === "dark" ? "#ccc" : undefined,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.mode === "dark" ? "#fff" : undefined,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme.palette.mode === "dark" ? "#666" : undefined,
                  "&.Mui-disabled": {
                    color: theme.palette.mode === "dark" ? "#666" : undefined,
                  },
                },
                "& .MuiInputBase-input": {
                  color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                  backgroundColor: theme.palette.mode === "dark" ? "#fff !important" : undefined,
                  "&.Mui-disabled": {
                    color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                    backgroundColor: theme.palette.mode === "dark" ? "#fff !important" : undefined,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: theme.palette.mode === "dark" ? "#666 !important" : undefined,
                },
                "& input": {
                  color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                  backgroundColor: theme.palette.mode === "dark" ? "#fff !important" : undefined,
                },
              }}
            />
            <TextField
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{ marginBottom: "10px" }}
              disabled={loading || mfaRequired}
              inputProps={{ tabIndex: 0, "aria-label": "Wachtwoord" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.mode === "dark" ? "#fff" : undefined,
                  "& fieldset": {
                    borderColor: theme.palette.mode === "dark" ? "#ccc" : undefined,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.mode === "dark" ? "#999" : undefined,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.mode === "dark" ? theme.palette.primary.main : undefined,
                  },
                  "&.Mui-disabled fieldset": {
                    borderColor: theme.palette.mode === "dark" ? "#ccc" : undefined,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.mode === "dark" ? "#fff" : undefined,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme.palette.mode === "dark" ? "#666" : undefined,
                  "&.Mui-disabled": {
                    color: theme.palette.mode === "dark" ? "#666" : undefined,
                  },
                },
                "& .MuiInputBase-input": {
                  color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                  backgroundColor: theme.palette.mode === "dark" ? "#fff !important" : undefined,
                  "&.Mui-disabled": {
                    color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                    backgroundColor: theme.palette.mode === "dark" ? "#fff !important" : undefined,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: theme.palette.mode === "dark" ? "#666 !important" : undefined,
                },
                "& input": {
                  color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                  backgroundColor: theme.palette.mode === "dark" ? "#fff !important" : undefined,
                },
              }}
            />
            {mfaRequired && (
              <TextField
                label="Authenticatiecode"
                variant="outlined"
                fullWidth
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ marginBottom: "10px" }}
                disabled={loading}
                inputProps={{ tabIndex: 0, "aria-label": "Authenticatiecode" }}
                autoFocus
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: theme.palette.mode === "dark" ? "#f5f5f5" : undefined,
                    "& fieldset": {
                      borderColor: theme.palette.mode === "dark" ? "#ccc" : undefined,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.mode === "dark" ? "#999" : undefined,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.mode === "dark" ? theme.palette.primary.main : undefined,
                    },
                    "&.Mui-disabled fieldset": {
                      borderColor: theme.palette.mode === "dark" ? "#ccc" : undefined,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.mode === "dark" ? "#f5f5f5" : undefined,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.mode === "dark" ? "#666" : undefined,
                    "&.Mui-disabled": {
                      color: theme.palette.mode === "dark" ? "#666" : undefined,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                    backgroundColor: theme.palette.mode === "dark" ? "#f5f5f5 !important" : undefined,
                    "&.Mui-disabled": {
                      color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                      backgroundColor: theme.palette.mode === "dark" ? "#f5f5f5 !important" : undefined,
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: theme.palette.mode === "dark" ? "#666 !important" : undefined,
                  },
                  "& input": {
                    color: theme.palette.mode === "dark" ? "#000 !important" : undefined,
                    backgroundColor: theme.palette.mode === "dark" ? "#f5f5f5 !important" : undefined,
                  },
                }}
              />
            )}
            <a
              href="https://deltarefundsolutions.com/Contact"
              className="forgot-password"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: "'Helvetica Neue Light', sans-serif", marginBottom: "40px", alignSelf: "flex-start" }}
            >
              Wachtwoord vergeten?
            </a>
            <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Button type="submit" className="login-button" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : mfaRequired ? "Verifiëren" : "Inloggen"}
              </Button>
            </div>
          </form>

          {/* <div className="extra-links extra-link-container">
            <a
              href="https://deltarefundsolutions.com/Contact"
              className="extra-link"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: "'Helvetica Neue Light', sans-serif" }}
            >
              U bent al klant, maar kunt niet inloggen?
            </a>
            <a
              href="https://deltarefundsolutions.com/Contact"
              className="extra-link"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: "'Helvetica Neue Light', sans-serif" }}
            >
              U wilt klant worden?
            </a>
          </div> */}
        </div>
      </div>
      <div className="right-pane">{/* Background image set via CSS */}</div>
    </div>
  );
}

export default SignIn;
